import { Table, Card, Button, Dropdown, Menu, Drawer, Badge } from "antd"
import "./tableBox.scss"
import {
  getStoredPageSize,
  storePageSize,
} from "../../Utils/TableUtils/TableUtils"
import { useEffect, useMemo, useState } from "react"

import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import dayjs from "dayjs"
import AssetPath from "../../AssetPath/AssetPath"
import { useUiPrefs } from "../../Utils/UiPrefsContext"
import EmptyState from "../EmptyState/EmptyState"
import apiClient from "../../Utils/apiClient"
import {
  FilterOutlined,
  CloseOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"

const TableBox = ({
  page,
  columns,
  data,
  title,
  loading = false,
  pagination = true,
  shadow = true,
  scroll,
  rowSelection,
  bordered = false,
  onChange,
  extra = null,
  exportable = false,
  exportOptions,
  enableFilter = false,
  filtercontent = null,
  onFilter,
  onClear,
  filters,
  onRow,
  rowKey,
  className,
  expandable,
}) => {
  const { density } = useUiPrefs()
  const handleChange = (pagination) => {
    if (pagination?.pageSize) {
      storePageSize(page, pagination.pageSize)
    }
    onChange?.(pagination)
  }

  const [drawerOpen, setDrawerOpen] = useState(false)

  const getActiveFilterCount = (params = {}) => {
    return Object.entries(params).filter(([key, val]) => {
      if (["page", "pageSize"].includes(key)) return false
      // return val !== null && val !== undefined && val !== ""
      if (val === null || val === undefined || val === "") return false

      // Exclude empty array
      if (Array.isArray(val) && val.length === 0) return false

      // Exclude empty object
      if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        Object.keys(val).length === 0
      )
        return false

      return true
    }).length
  }

  const activeFilterCount = useMemo(() => {
    return getActiveFilterCount(exportOptions?.params || filters || {})
  }, [exportOptions?.params, filters])

  const fetchExportData = async () => {
    try {
      const params = {
        ...exportOptions.params,
        page: exportOptions.params?.page ?? 1,
        pageSize: exportOptions.params?.pageSize ?? "99999",
      }

      const options = exportOptions.options || {}
      const res = await apiClient(
        exportOptions?.endpoint,
        exportOptions?.method || "GET",
        params,
        options
      )
      const rawData = res?.data || res.result || []

      if (exportOptions?.onExportData) {
        exportOptions.onExportData(rawData)
      }

      return exportOptions.format ? exportOptions.format(rawData) : rawData
    } catch (err) {
      console.error("Error fetching export data", err)
      return []
    }
  }

  const exportToExcel = () => {
    fetchExportData().then((exportData) => {
      const wb = XLSX.utils.book_new()
      const ignoreKeys = exportOptions?.excelWithout || []

      const formattedData = exportData.map((row) => {
        const exportRow = {}

        exportOptions.header.forEach((header, i) => {
          const key = Object.keys(row)[i] || header

          if (ignoreKeys.includes(key)) return

          let value = row[header] ?? row[key] ?? Object.values(row)[i] ?? ""

          if (Array.isArray(value)) {
            if (value.every((v) => typeof v === "object" && v?.hostname)) {
              value = value.map((v) => v.hostname).join(", ")
            } else {
              value = value.join(", ")
            }
          } else if (typeof value === "object" && value !== null) {
            value = JSON.stringify(value)
          }

          exportRow[header] = value
        })

        return exportRow
      })

      const ws = XLSX.utils.json_to_sheet(formattedData)

      const colWidths = []
      formattedData.forEach((row) => {
        Object.values(row).forEach((value, index) => {
          const length = value ? value.toString().length : 10
          if (!colWidths[index] || colWidths[index] < length) {
            colWidths[index] = Math.min(length + 2, 50)
          }
        })
      })
      ws["!cols"] = colWidths.map((w) => ({ width: w }))

      XLSX.utils.book_append_sheet(wb, ws, "Data")

      const filename = `${
        exportOptions?.fileName || title || exportOptions?.btnTitle || "export"
      }-${new Date().toISOString().split("T")[0]}.xlsx`

      XLSX.writeFile(wb, filename)
    })
  }

  const exportToPDF = () => {
    fetchExportData().then((exportData) => {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })

      let headers, rows
      const ignoreKeys = exportOptions?.pdfWithout || []

      headers = exportOptions.header.filter(
        (h, index) =>
          !ignoreKeys.includes(Object.keys(exportData[0] || {})[index])
      )

      rows = exportData.map((row) => {
        return Object.entries(row)
          .filter(([key]) => !ignoreKeys.includes(key))
          .map(([_, value]) => {
            if (value === null || value === undefined || value === "") {
              return "-"
            }
            return value
          })
      })

      const pdfTitle =
        exportOptions?.fileName || title || exportOptions?.btnTitle || "Export"

      const img = new Image()
      img.src = AssetPath.brand.appIcon
      img.crossOrigin = "Anonymous"

      img.onload = function () {
        const canvas = document.createElement("canvas")
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0)
        const base64 = canvas.toDataURL("image/png")

        const pageWidth = doc.internal.pageSize.getWidth()

        const logoWidth = 15
        const logoHeight = 12
        const logoY = 7

        doc.setFontSize(14)
        const textWidth =
          (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
          doc.internal.scaleFactor

        const gap = 3
        const totalWidth = logoWidth + gap + textWidth

        const startX = (pageWidth - totalWidth) / 2

        doc.addImage(base64, "PNG", startX, logoY, logoWidth, logoHeight)
        doc.text(pdfTitle, startX + logoWidth + gap, logoY + 9)

        autoTable(doc, {
          head: [headers],
          body: rows,
          styles: {
            fontSize: 6,
            cellWidth: "wrap",
            overflow: "linebreak",
            cellPadding: 1,
          },
          headStyles: {
            fillColor: [66, 139, 202],
            fontSize: 7,
            fontStyle: "bold",
          },
          margin: { top: 25 },
          startY: 25,

          didDrawPage: function () {
            const pw = doc.internal.pageSize.getWidth()
            const ph = doc.internal.pageSize.getHeight()

            const x = pw / 2 - 85
            const y = ph / 2 - 60

            doc.setGState(new doc.GState({ opacity: 0.12 }))
            doc.addImage(base64, "PNG", x, y, 170, 120)
            doc.setGState(new doc.GState({ opacity: 1 }))
          },
        })

        const pageCount = doc.internal.getNumberOfPages()
        doc.setPage(pageCount)
        doc.setFontSize(8)
        doc.setTextColor(100)
        const footerText =
          "© 2026 Xpanstech Solutions Pvt Ltd. All rights reserved.\n" +
          '"Xpanse" is a product of Xpanstech Solutions Pvt Ltd. Unauthorized copying, distribution, modification, or use of this software or its components is strictly prohibited.'
        const footerY = doc.internal.pageSize.getHeight() - 10

        doc.text(footerText, pageWidth / 2, footerY, { align: "center" })

        const filename = `${pdfTitle}-${dayjs().format("DD-MM-YYYY")}.pdf`
        doc.save(filename)
      }
    })
  }

  const menu = (
    <Menu>
      <Menu.Item key="excel" onClick={exportToExcel}>
        Export to Excel
      </Menu.Item>
      <Menu.Item key="pdf" onClick={exportToPDF}>
        Export to PDF
      </Menu.Item>
    </Menu>
  )

  return (
    <Card
      className={[
        "table-card",
        shadow ? "shadow-sm" : "",
        "rounded-lg border-0",
        title ? "" : "head-0",
        className || "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={title}
      extra={
        <div className="d-flex align-items-center gap-2">
          {enableFilter && (
            <>
              <Badge
                count={activeFilterCount}
                offset={[-2, 8]}
                style={{
                  backgroundColor: "#fff",
                  boxShadow: "0 0 0 0px #fff inset",
                  color: "red",
                  borderRadius: "50%",
                }}
              >
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setDrawerOpen(true)}
                />
              </Badge>

              <Drawer
                className="filter-drawer"
                title={
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <span>Apply Filter</span>
                    <CloseOutlined
                      onClick={() => setDrawerOpen(false)}
                      className="rounded-circle p-1"
                      style={{
                        color: "rgba(0,0,0,0.6)",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                }
                mask={false}
                placement="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                closable={false}
                width={300}
              >
                <div className="d-flex flex-column justify-content-between h-100">
                  <>
                    <div className="p-3 pb-0">{filtercontent}</div>
                    {filtercontent && (
                      <div className="p-3 d-flex gap-2 justify-content-end">
                        <Button
                          size="small"
                          className="btn-form-filter btn-form-filter--clear rounded-pill"
                          onClick={() => {
                            onFilter()
                            setDrawerOpen(false)
                          }}
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          // disabled={activeFilterCount === 0}
                        >
                          Apply
                        </Button>
                        <Button
                          size="small"
                          className="btn-form-filter rounded-pill"
                          onClick={() => {
                            onClear()
                            setDrawerOpen(false)
                          }}
                          icon={<CloseOutlined />}
                          // disabled={activeFilterCount === 0}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </>
                </div>
              </Drawer>
            </>
          )}

          {exportable && (
            <Dropdown
              overlay={menu}
              placement="bottomRight"
              arrow
              trigger={["click"]}
            >
              <Button
                icon={<DownloadOutlined />}
                onClick={() => fetchExportData()}
              >
                {exportOptions?.btnTitle || "Export"}
              </Button>
            </Dropdown>
          )}
          {extra}
        </div>
      }
    >
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={
          rowKey ||
          ((record) => record.key || record.id || record._id || Math.random())
        }
        pagination={pagination}
        scroll={scroll || { x: "max-content" }}
        rowSelection={rowSelection}
        onRow={onRow}
        expandable={expandable}
        bordered={bordered}
        size={density === "compact" ? "small" : "middle"}
        onChange={handleChange}
        sticky={{ offsetHeader: 0 }}
        locale={{
          emptyText: (
            <EmptyState
              title="No records found"
              description="Try a different filter, or clear search to see all results."
            />
          ),
        }}
      />
    </Card>
  )
}

export default TableBox
